const { getElement } = window.BAO.Utils
class LazyVideo extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: contents;

        opacity: 0;
      
        transition: opacity 0.5s ease;
      }
      :host([loaded]) {
        opacity: 1;
      }
    `
  }

  connectedCallback () {
    super.connectedCallback()

    this.video = getElement('video', { context: this })

    // Always create and observe the video for pause/resume
    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(this.onEntry.bind(this), {
      threshold: 0,
    })
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(document, 'DOMContentLoaded', this.onLoad.bind(this))
    this.listeners.add(window, 'resize', this.onResize.bind(this))

    // Handle back/forward cache (bfcache) restores in Safari
    // when a page is restored from bfcache the media/MediaSource can
    // be left in a bad state and Hls.js may need re-initialization.
    this.onPageShow = this.onPageShow.bind(this)
    this.onPageHide = this.onPageHide.bind(this)
    window.addEventListener('pageshow', this.onPageShow)
    window.addEventListener('pagehide', this.onPageHide)
  }

  async onLoad () {
    // Always observe the video for pause/resume
    this.observer.observe(this.video)

    if (!this.deferVideo) {
      const hlsSource = this.hlsSource
      if (!hlsSource) return
      this.handleHlsVideo(this.hlsSource)
    }
  }

  onPageHide (event) {
    if (this.hls) {
      try {
        this.hls.stopLoad()
        this.hls.detachMedia()
      } catch (e) {
        console.error(e)
      }
    }

    // Pause video to avoid playback after restore
    try {
      if (this.video && !this.video.paused) this.video.pause()
    } catch (e) {
      console.error(e)
    }
  }

  onPageShow (event) {
    // If the page was restored from bfcache (event.persisted) then
    // re-create or reload the Hls instance to ensure MediaSource is fresh.
    if (!event || !event.persisted) return

    // If we have an Hls instance, destroy it so we can reinitialize.
    if (this.hls) {
      try {
        this.hls.destroy()
      } catch (e) {
        console.error(e)
      }
      this.hls = null
    }

    // Re-observe the video (intersection observer may be paused) and
    // reload sources so the video can start fetching segments again.
    try {
      if (this.observer && this.video) this.observer.observe(this.video)

      if (this.video.dataset.hls === 'true') {
        this.handleHlsVideo(this.hlsSource)
      } else if (this.deferVideo) {
        for (const source of Array.from(this.video.children)) {
          if (source.tagName === 'SOURCE' && source.dataset.src) {
            source.src = source.dataset.src
          }
        }
        this.video.load()
      }
    } catch (e) {
      console.error(e)
    }
  }

  async onEntry ([entry], observer) {
    if (entry.isIntersecting) {
      // If deferred, load sources when in view
      if (this.deferVideo && this.video.readyState === 0) {
        if (this.video.dataset.hls === 'true') {
          for (const source of Array.from(this.video.children)) {
            if (source.tagName === 'SOURCE' && source.dataset.src) {
              source.src = source.dataset.src
            }
            if (source.type === 'application/x-mpegURL') {
              await this.handleHlsVideo(source.src)
            }
          }
        } else {
          if (this.video.dataset.hls === 'true') {
            await this.handleHlsVideo(this.hlsSource)
          } else if (!this.video.src) {
            for (const source of Array.from(this.video.children)) {
              if (source.tagName === 'SOURCE' && source.dataset.src) {
                source.src = source.dataset.src
              }
            }
          }
          this.video.load()
        }
        this.setAttribute('loaded', '')
      }
      // Resume video if paused and autoplay is not disabled and video shouldn't play once
      if (
        this.video.paused &&
        !this.dataset.disableAutoplay &&
        this.video.loop
      ) {
        this.video.play().catch(() => {
          // If play is blocked (e.g. mobile), allow user interaction to trigger
          document.addEventListener(
            'touchstart',
            () => {
              if (this.video.paused) {
                this.video.play()
              }
            },
            { once: true },
          )
        })
      }
    } else {
      // Pause video when out of view
      if (!this.video.paused) {
        this.video.pause()
      }
    }
  }

  onResize () {
    if (this.video.dataset.hls === 'true') {
      this.handleHlsVideo(this.hlsSource)
    } else {
      for (const source of Array.from(this.video.children)) {
        if (source.type === 'application/x-mpegURL') {
          this.handleHlsVideo(source)
        }
      }
    }
  }

  async handleHlsVideo (src) {
    if (!src) return

    if (this.hls) return

    /* global Hls */
    // eslint-disable-next-line import/no-unresolved
    await import('https://cdn.jsdelivr.net/npm/hls.js@latest')

    this.hls = new Hls({
      enableWorker: true,
      capLevelToPlayerSize: true,
      testBandwidth: true,
      startLevel: -1,
      autoStartLoad: true,
      maxBufferLength: 2,
      maxMaxBufferLength: 2,
    })

    if (!src) return

    this.hls.loadSource(src)
    this.hls.attachMedia(this.video)

    // On loop, force quality re-evaluation to fetch higher quality segments
    // if network conditions have improved since initial load
    // Only triggers if not already at max quality to avoid unnecessary bandwidth usage
    this.video.addEventListener('seeking', () => {
      if (this.video.loop && this.video.currentTime < 0.5 && this.hls) {
        const currentLevel = this.hls.currentLevel
        const maxLevel = this.hls.levels.length - 1

        // Only re-evaluate if we're not already at max quality
        // currentLevel === -1 means auto mode (HLS.js manages quality)
        // currentLevel === maxLevel means we're already at highest quality
        if (currentLevel !== -1 && currentLevel < maxLevel) {
          this.hls.currentLevel = -1
        }
      }
    })
    if (this.dataset.waitforinteraction === 'true') {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.video.play()
            }
          })
        },
        { threshold: 0.7 },
      )
      observer.observe(this.video)
    } else if (this.dataset.displayCustomVideoControls === 'true') {
      this.video.pause()
      this.videoControls = getElement('.rsp-Image_CustomControls', {
        context: this,
      })
      this.videoControls.addEventListener('click', () => {
        this.video.muted = false
        this.video.loop = false
        this.video.play()
        this.videoControls.classList.add('rsp-Image_CustomControls-Hide')
      })
    } else {
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const isVisible =
          typeof this.video.checkVisibility === 'function'
            ? this.video.checkVisibility()
            : true
        if (!isVisible) return
        this.video.play()
      })
    }
  }

  get deferVideo () {
    return this.dataset.defer === 'true'
  }

  get hlsSource () {
    return this.video.dataset.src
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideo)
}
